import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, DollarSign, Loader2, CheckCircle } from "lucide-react";
import { getInsuranceQuoteRequests, accessInsuranceLead } from "@/Calls/callInsuranceAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface LeadRequest {
    request_id: number;
    vehicle_vin: string | null;
    vehicle_year: number | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
    vehicle_type: string | null;
    consent_at: string | null;
    created_at: string;
    status: string;
    lead_accesses: { access_id: number }[];
}

export default function InsuranceQuoteRequestsPage() {
    const [requests, setRequests] = useState<LeadRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [pulling, setPulling] = useState<number | null>(null);
    const [pulledData, setPulledData] = useState<Record<number, any>>({});
    const [confirmId, setConfirmId] = useState<number | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        getInsuranceQuoteRequests()
            .then((d) => setRequests(d.requests ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const handlePullLead = async (requestId: number) => {
        setPulling(requestId);
        setConfirmId(null);
        try {
            const res = await accessInsuranceLead(requestId);
            if (res.ok) {
                setPulledData((prev) => ({ ...prev, [requestId]: res }));
                setRequests((prev) =>
                    prev.map((r) =>
                        r.request_id === requestId
                            ? { ...r, lead_accesses: [{ access_id: res.access.access_id }] }
                            : r
                    )
                );
            }
        } catch (e: any) {
            alert(e.message ?? "Failed to pull lead");
        } finally {
            setPulling(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">Quote Requests</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    {requests.length} lead{requests.length !== 1 ? "s" : ""} available · $35.00 per lead pulled
                </p>
            </div>

            {requests.length === 0 ? (
                <Card className="p-12 text-center border-dashed">
                    <Car className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">No leads available</p>
                    <p className="text-sm text-muted-foreground mt-1">Check back soon — new requests are posted as customers consent to insurance quotes.</p>
                </Card>
            ) : (
                <div className="space-y-4">
                    {requests.map((r) => {
                        const alreadyPulled = r.lead_accesses.length > 0;
                        const pulled = pulledData[r.request_id];

                        return (
                            <Card key={r.request_id} className="p-5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <p className="font-semibold text-lg">
                                                {r.vehicle_year} {r.vehicle_make} {r.vehicle_model}
                                            </p>
                                            {r.vehicle_type && (
                                                <Badge variant="outline" className="text-xs">{r.vehicle_type}</Badge>
                                            )}
                                        </div>
                                        <div className="text-sm text-muted-foreground space-y-1">
                                            {r.vehicle_vin && <p>VIN: {alreadyPulled ? r.vehicle_vin : `****${r.vehicle_vin.slice(-4)}`}</p>}
                                            <p>Request Date: {new Date(r.created_at).toLocaleDateString()}</p>
                                            {r.consent_at && <p>Consent Given: {new Date(r.consent_at).toLocaleDateString()}</p>}
                                        </div>

                                        {/* Customer data revealed after pulling */}
                                        {pulled && (
                                            <div className="mt-4 p-4 bg-muted rounded-lg space-y-2 text-sm">
                                                <p className="font-semibold text-green-600 flex items-center gap-1">
                                                    <CheckCircle className="h-4 w-4" /> Lead Unlocked
                                                </p>
                                                <div className="grid sm:grid-cols-2 gap-2">
                                                    <div>
                                                        <p className="text-muted-foreground">Customer</p>
                                                        <p className="font-medium">
                                                            {pulled.customer?.customer_fname} {pulled.customer?.customer_lname}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-muted-foreground">Email</p>
                                                        <p className="font-medium">{pulled.customer?.customer_email ?? "—"}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-muted-foreground">Phone</p>
                                                        <p className="font-medium">{pulled.customer?.customer_phoneno ?? "—"}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-muted-foreground">City</p>
                                                        <p className="font-medium">
                                                            {pulled.customer?.customer_home_city ?? "—"}
                                                            {pulled.customer?.customer_home_state ? `, ${pulled.customer.customer_home_state}` : ""}
                                                        </p>
                                                    </div>
                                                    {pulled.deal && (
                                                        <div>
                                                            <p className="text-muted-foreground">Vehicle</p>
                                                            <p className="font-medium">{pulled.deal.vYear} {pulled.deal.vMake} {pulled.deal.vModel}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2 min-w-[140px]">
                                        {alreadyPulled ? (
                                            <>
                                                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 justify-center">Lead Pulled</Badge>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => navigate(`/insurance/quote-requests/${r.request_id}/submit`)}
                                                >
                                                    Submit Quote
                                                </Button>
                                            </>
                                        ) : confirmId === r.request_id ? (
                                            <>
                                                <p className="text-xs text-center text-muted-foreground">$35.00 will be charged</p>
                                                <Button
                                                    size="sm"
                                                    className="bg-blue-600 text-white hover:bg-blue-700"
                                                    onClick={() => handlePullLead(r.request_id)}
                                                    disabled={pulling === r.request_id}
                                                >
                                                    {pulling === r.request_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Pull"}
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>Cancel</Button>
                                            </>
                                        ) : (
                                            <Button
                                                size="sm"
                                                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
                                                onClick={() => setConfirmId(r.request_id)}
                                            >
                                                <DollarSign className="h-4 w-4 mr-1" />
                                                Pull Lead ($35)
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
